import { createGlobalState, useDebounceFn, useLocalStorage } from "@vueuse/core";
import { omit } from "lodash";
import { computed, readonly, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  ExtendedAddressModal,
  saveAddressExtension,
  getAddressExtensionByAddressId,
  AddressSource,
} from "@/arad/modules/address-extension";
import { useWalletPayment } from "@/arad/modules/wallet";
import { createOrderFromCart as _createOrderFromCart } from "@/core/api/graphql";
import { useAnalytics, useHistoricalEvents, useThemeContext } from "@/core/composables";
import { initializePayment } from "@/core/api/graphql/payment";
import { AddressType, ProductType } from "@/core/enums";
import { globals } from "@/core/globals";
import { isEqualAddresses, Logger } from "@/core/utilities";
import { createSharedComposableByArgs } from "@/core/utilities/composables";
import { useUser, useUserAddresses } from "@/shared/account";
import { useFullCart, EXTENDED_DEBOUNCE_IN_MS } from "@/shared/cart";
import { useOrganizationAddresses } from "@/shared/company";
import { useModal } from "@/shared/modal";
import { useNotifications } from "@/shared/notification";
import { PaymentMethodGroupType, usePayment } from "@/shared/payment";
import { BOPIS_CODE } from "./useBopis";
import type { IAddressExtensionFormState, IAddressExtensionInput } from "@/arad/modules/address-extension";
import type {
  CartAddressType,
  CustomerOrderType,
  InputAddressType,
  InputPaymentType,
  MemberAddressType,
  PaymentMethodType,
} from "@/core/api/graphql/types";
import type { AnyAddressType } from "@/core/types";
import SelectAddressModal from "@/shared/checkout/components/select-address-modal.vue";

// Key for storing last used shipping extension in localStorage (survives clearCart)
const LAST_SHIPPING_EXTENSION_KEY = "checkout_last_shipping_extension";
const LAST_BILLING_EXTENSION_KEY = "checkout_last_billing_extension";
// Extension localStorage entries expire after 24 hours to prevent stale data
const EXTENSION_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// Extended type for localStorage that includes metadata for safety
interface ICachedExtension {
  data: IAddressExtensionFormState;
  userId: string;
  timestamp: number;
}

const useGlobalCheckout = createGlobalState(() => {
  const loading = ref(false);
  const initialized = ref(false);
  const billingAddressEqualsShipping = ref(true);
  const placedOrder = shallowRef<CustomerOrderType | null>(null);

  const commentChanging = ref(false);
  const _comment = ref<string>();

  const purchaseOrderNumberChanging = ref(false);
  const _purchaseOrderNumber = ref<string>();

  // Billing extension validation state
  const billingExtensionValid = ref(true); // Default to true, will be validated on checkout
  const skipValidationOnce = ref(false); // Flag to skip validation once after address ID change
  const billingExtensionError = ref<string | null>(null);

  // Track source member address IDs when user explicitly selects an address
  // This allows us to sync extension data back to the correct member address
  const sourceMemberAddressIds = ref<{ shipping?: string; billing?: string }>({});

  function clearState() {
    _comment.value = undefined;
    billingExtensionValid.value = true;
    billingExtensionError.value = null;
    skipValidationOnce.value = false;
    sourceMemberAddressIds.value = {};
  }

  /**
   * Sets the source member address ID for tracking.
   * Called when user explicitly selects an address (from checkout or header dropdown).
   * @param type - 'shipping' or 'billing'
   * @param memberAddressId - The member address ID to track
   */
  function setSourceMemberAddressId(type: "shipping" | "billing", memberAddressId: string | undefined) {
    if (memberAddressId) {
      sourceMemberAddressIds.value[type] = memberAddressId;
      Logger.info("useGlobalCheckout", `[SET SOURCE ID] Tracked source member address for ${type}: ${memberAddressId}`);
    }
  }

  return {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    commentChanging,
    _comment,
    purchaseOrderNumberChanging,
    _purchaseOrderNumber,
    initialized,
    billingExtensionValid,
    billingExtensionError,
    skipValidationOnce,
    sourceMemberAddressIds,
    setSourceMemberAddressId,
    clearState,
  };
});

/**
 * Sets the source member address ID for tracking.
 * Use this when selecting a member address from outside checkout (e.g., header dropdown).
 * This ensures that when user fills billing info in checkout, it can be synced back to the correct member address.
 */
export function setSourceMemberAddressIdForShipping(memberAddressId: string | undefined) {
  const { setSourceMemberAddressId } = useGlobalCheckout();
  setSourceMemberAddressId("shipping", memberAddressId);
  // Also set billing when billingAddressEqualsShipping is common default
  setSourceMemberAddressId("billing", memberAddressId);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function _useCheckout(cartId?: string) {
  const { analytics } = useAnalytics();
  const { t } = useI18n();
  const route = useRoute();
  const notifications = useNotifications();
  const { openModal, closeModal } = useModal();
  const router = useRouter();
  const { user, isAuthenticated, isCorporateMember } = useUser();
  const {
    addresses: personalAddresses,
    fetchAddresses: fetchPersonalAddresses,
    addOrUpdateAddresses: addOrUpdatePersonalAddresses,
  } = useUserAddresses();
  const {
    addresses: organizationsAddresses,
    fetchAddresses: fetchOrganizationAddresses,
    addOrUpdateAddresses: addOrUpdateOrganizationAddresses,
  } = useOrganizationAddresses(user.value.contact?.organizationId || "");
  const {
    refetch: refetchCart,
    cart,
    selectedLineItems,
    selectCartItems,
    shipment,
    payment,
    availableShippingMethods,
    availablePaymentMethods,
    hasValidationErrors,
    allItemsAreDigital,
    updateShipment,
    removeShipment,
    updatePayment,
    changeComment,
    updatePurchaseOrderNumber,
  } = useFullCart();
  const {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    commentChanging,
    _comment,
    purchaseOrderNumberChanging,
    _purchaseOrderNumber,
    clearState: clearGlobalCheckoutState,
    initialized,
    billingExtensionValid,
    billingExtensionError,
    skipValidationOnce,
    sourceMemberAddressIds,
  } = useGlobalCheckout();
  const { themeContext } = useThemeContext();

  // localStorage for last used extension data (survives clearCart for logged-in users)
  // Includes userId and timestamp to prevent cross-user contamination and stale data
  const lastShippingExtensionCache = useLocalStorage<ICachedExtension | null>(LAST_SHIPPING_EXTENSION_KEY, null);
  const lastBillingExtensionCache = useLocalStorage<ICachedExtension | null>(LAST_BILLING_EXTENSION_KEY, null);

  // Helper to get valid cached extension (checks userId and TTL)
  function getValidCachedExtension(cache: ICachedExtension | null): IAddressExtensionFormState | null {
    if (!cache) return null;
    const currentUserId = user.value?.id;
    if (!currentUserId || cache.userId !== currentUserId) {
      Logger.info("useCheckout", "[CACHE] Cached extension userId mismatch, ignoring", {
        cachedUserId: cache.userId,
        currentUserId,
      });
      return null;
    }
    const age = Date.now() - cache.timestamp;
    if (age > EXTENSION_CACHE_TTL_MS) {
      Logger.info("useCheckout", "[CACHE] Cached extension expired", { ageHours: Math.round(age / 3600000) });
      return null;
    }
    return cache.data;
  }

  // Helper to create cached extension with metadata
  function createCachedExtension(data: IAddressExtensionFormState): ICachedExtension | null {
    const currentUserId = user.value?.id;
    if (!currentUserId) {
      Logger.warn("useCheckout", "[CACHE] Cannot cache extension: no user ID");
      return null;
    }
    return {
      data,
      userId: currentUserId,
      timestamp: Date.now(),
    };
  }

  // Clear extension cache (called after successful order)
  function clearExtensionCache(): void {
    lastShippingExtensionCache.value = null;
    lastBillingExtensionCache.value = null;
    Logger.info("useCheckout", "[CACHE] Extension cache cleared");
  }
  const { pushHistoricalEvent } = useHistoricalEvents();
  const { finalizePayment } = usePayment();

  const deliveryAddress = computed(() => shipment.value?.deliveryAddress);
  const isShippingMethodBopis = computed(() => shipment.value?.shipmentMethodCode === BOPIS_CODE);

  const billingAddress = computed(() =>
    !allItemsAreDigital.value &&
    !isShippingMethodBopis.value &&
    billingAddressEqualsShipping.value &&
    deliveryAddress.value
      ? deliveryAddress.value
      : payment.value?.billingAddress,
  );
  const shipmentMethod = computed(() =>
    availableShippingMethods.value.find(
      (item) => item.id === shipment.value?.shipmentMethodCode + "_" + shipment.value?.shipmentMethodOption,
    ),
  );
  const paymentMethod = computed(() =>
    availablePaymentMethods.value.find((item) => item.code === payment.value?.paymentGatewayCode),
  );

  const changeCommentDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.comment !== value) {
      await changeComment(value);
    }
    commentChanging.value = false;
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updatePurchaseOrderNumberDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.purchaseOrderNumber !== value) {
      await updatePurchaseOrderNumber(value);
    }
    purchaseOrderNumberChanging.value = false;
  }, EXTENDED_DEBOUNCE_IN_MS);

  const comment = computed({
    get: () => _comment.value ?? cart.value?.comment ?? "",
    set: (value: string) => {
      commentChanging.value = true;
      _comment.value = value;
      void changeCommentDebounced(value.trim());
    },
  });

  const purchaseOrderNumber = computed({
    get: () => _purchaseOrderNumber.value ?? cart.value?.purchaseOrderNumber ?? "",
    set: (value: string) => {
      purchaseOrderNumberChanging.value = true;
      _purchaseOrderNumber.value = value;
      void updatePurchaseOrderNumberDebounced(value);
    },
  });

  const isValidDeliveryAddress = computed<boolean>(() => !!shipment.value?.deliveryAddress);
  const isValidBillingAddress = computed<boolean>(
    () =>
      (!allItemsAreDigital.value && !isShippingMethodBopis.value && billingAddressEqualsShipping.value) ||
      !!payment.value?.billingAddress,
  );
  const isValidShipmentMethod = computed<boolean>(() => !!shipment.value?.shipmentMethodCode);
  const isValidPaymentMethod = computed<boolean>(() => !!payment.value?.paymentGatewayCode);
  const isValidShipment = computed<boolean>(() => isValidDeliveryAddress.value && isValidShipmentMethod.value);
  const isValidPayment = computed<boolean>(() => isValidBillingAddress.value && isValidPaymentMethod.value);
  // Include billing extension validation in checkout validity
  const isValidCheckout = computed<boolean>(() =>
    allItemsAreDigital.value
      ? isValidPayment.value && !hasValidationErrors.value && billingExtensionValid.value
      : isValidShipment.value && isValidPayment.value && !hasValidationErrors.value && billingExtensionValid.value,
  );

  const selectedPaymentMethodGroupType = computed<string | undefined>(() => {
    // First check placed order's payment method directly (most reliable after order creation).
    // Skip WalletBalance payments — they are always Alternative and don't drive canPayNow routing.
    const primaryOrderPayment = placedOrder.value?.inPayments?.find(
      (p) => p.paymentMethod?.code !== "WalletBalance",
    );
    const placedOrderPaymentMethodGroupType = primaryOrderPayment?.paymentMethod?.paymentMethodGroupType;
    if (placedOrderPaymentMethodGroupType !== undefined) {
      return PaymentMethodGroupType[placedOrderPaymentMethodGroupType];
    }
    // Fallback to looking up from available methods
    const paymentMethodCode = payment.value?.paymentGatewayCode;
    return availablePaymentMethods.value.find((item) => item.code === paymentMethodCode)?.paymentMethodGroupType;
  });

  const canPayNow = computed<boolean>(
    () =>
      isAuthenticated.value &&
      !!selectedPaymentMethodGroupType.value &&
      selectedPaymentMethodGroupType.value !== PaymentMethodGroupType[PaymentMethodGroupType.Manual],
  );

  const addresses = computed<AnyAddressType[]>(() => {
    return isCorporateMember.value ? organizationsAddresses.value : personalAddresses.value;
  });

  const isPurchaseOrderNumberEnabled = computed<boolean>(
    () =>
      !!selectedPaymentMethodGroupType.value &&
      selectedPaymentMethodGroupType.value === PaymentMethodGroupType[PaymentMethodGroupType.Manual],
  );

  const allOrderItemsAreDigital = computed(() =>
    placedOrder.value?.items?.every((item) => item.productType === ProductType.Digital),
  );

  function isExistAddress(address: AnyAddressType): boolean {
    return addresses.value.some((item) => isEqualAddresses(item, address));
  }

  async function setPaymentMethod(method: PaymentMethodType): Promise<void> {
    await updatePayment({
      id: payment.value?.id,
      paymentGatewayCode: method.code,
    });
  }

  watch(allItemsAreDigital, async (value, previousValue) => {
    // Update defaults if state changed not on initialization
    if (previousValue !== undefined && value !== undefined && previousValue !== value) {
      await setCheckoutDefaults();
    }
  });

  async function setCheckoutDefaults(): Promise<void> {
    if (allItemsAreDigital.value && shipment.value) {
      await removeShipment(shipment.value.id);
    }

    // Create at initialization to prevent duplication due to lack of id
    if (!allItemsAreDigital.value && !shipment.value) {
      await updateShipment({});
    }

    if (!payment.value) {
      await updatePayment({});
    }
  }

  /**
   * Ensures cart addresses have extension data by copying from member addresses if needed.
   * This handles the case when a cart is auto-populated from member profile but
   * extension data exists only in AddressSource.Member, not AddressSource.Cart.
   */
  async function ensureCartAddressExtensions(): Promise<void> {
    if (!isAuthenticated.value) {
      return;
    }

    // Wait for addresses to be fetched
    await fetchAddresses();

    const cartAddresses: Array<{ address: CartAddressType | undefined; type: string }> = [
      { address: shipment.value?.deliveryAddress, type: "shipping" },
      { address: payment.value?.billingAddress, type: "billing" },
    ];

    for (const { address: cartAddress, type } of cartAddresses) {
      if (!cartAddress?.id) {
        continue;
      }

      try {
        // Check if cart address already has extension data
        const cartExtension = await getAddressExtensionByAddressId(cartAddress.id, AddressSource.Cart);

        // If extension exists and has personType, skip (already has data)
        if (cartExtension?.personType) {
          Logger.info("useCheckout", `[INIT] ${type} address ${cartAddress.id} already has extension data`);
          continue;
        }

        // Find matching member address by comparing address fields
        const matchingMemberAddress = addresses.value.find((memberAddr) => isEqualAddresses(memberAddr, cartAddress));

        if (!matchingMemberAddress?.id) {
          Logger.info("useCheckout", `[INIT] No matching member address found for ${type} address`);
          // Log cart address and member addresses for debugging
          Logger.debug("useCheckout", `[INIT] Cart address fields:`, {
            firstName: cartAddress.firstName,
            lastName: cartAddress.lastName,
            city: cartAddress.city,
            line1: cartAddress.line1,
            countryCode: cartAddress.countryCode,
            postalCode: cartAddress.postalCode,
          });
          Logger.debug(
            "useCheckout",
            `[INIT] Available member addresses (${addresses.value.length}):`,
            addresses.value.map((a) => ({
              id: a.id,
              firstName: a.firstName,
              lastName: a.lastName,
              city: a.city,
              line1: a.line1,
            })),
          );

          // Fallback 1: Try to find extension by contact's selectedAddressId
          const selectedAddressId = user.value?.contact?.selectedAddressId;
          if (selectedAddressId) {
            const selectedMemberExtension = await getAddressExtensionByAddressId(
              selectedAddressId,
              AddressSource.Member,
            );
            if (selectedMemberExtension?.personType) {
              Logger.info(
                "useCheckout",
                `[INIT] Found extension via selectedAddressId ${selectedAddressId}, copying to cart address ${cartAddress.id}`,
              );
              const cartExtensionInput: IAddressExtensionInput = {
                addressId: cartAddress.id,
                addressSource: AddressSource.Cart,
                guild: selectedMemberExtension.guild || undefined,
                storeName: selectedMemberExtension.storeName || undefined,
                latitude: selectedMemberExtension.latitude ?? undefined,
                longitude: selectedMemberExtension.longitude ?? undefined,
                personType: selectedMemberExtension.personType ?? undefined,
                naturalPersonName: selectedMemberExtension.naturalPersonName || undefined,
                naturalPersonFamily: selectedMemberExtension.naturalPersonFamily || undefined,
                naturalPersonNationalCode: selectedMemberExtension.naturalPersonNationalCode || undefined,
                nationalCardAssetId: selectedMemberExtension.nationalCardAssetId ?? undefined,
                legalPersonName: selectedMemberExtension.legalPersonName || undefined,
                legalPersonNationalCode: selectedMemberExtension.legalPersonNationalCode || undefined,
                legalPersonEconomicalCode: selectedMemberExtension.legalPersonEconomicalCode ?? undefined,
                businessLicenseAssetId: selectedMemberExtension.businessLicenseAssetId ?? undefined,
              };
              await saveAddressExtension(cartExtensionInput);
              Logger.info(
                "useCheckout",
                `[INIT] Successfully copied extension via selectedAddressId to ${type} address`,
              );
              continue;
            }
          }

          // Fallback 2: Try to restore from localStorage (for addresses created in cart that survive clearCart)
          const cachedExtension =
            type === "shipping" ? lastShippingExtensionCache.value : lastBillingExtensionCache.value;
          const lastExtension = getValidCachedExtension(cachedExtension);
          if (lastExtension?.personType) {
            Logger.info(
              "useCheckout",
              `[INIT] Found valid extension in localStorage for ${type}, copying to cart address ${cartAddress.id}`,
            );
            const cartExtensionInput: IAddressExtensionInput = {
              addressId: cartAddress.id,
              addressSource: AddressSource.Cart,
              guild: lastExtension.guild || undefined,
              storeName: lastExtension.storeName || undefined,
              latitude: lastExtension.latitude ?? undefined,
              longitude: lastExtension.longitude ?? undefined,
              personType: lastExtension.personType ?? undefined,
              naturalPersonName: lastExtension.naturalPersonName || undefined,
              naturalPersonFamily: lastExtension.naturalPersonFamily || undefined,
              naturalPersonNationalCode: lastExtension.naturalPersonNationalCode || undefined,
              nationalCardAssetId: lastExtension.nationalCardAssetId ?? undefined,
              legalPersonName: lastExtension.legalPersonName || undefined,
              legalPersonNationalCode: lastExtension.legalPersonNationalCode || undefined,
              legalPersonEconomicalCode: lastExtension.legalPersonEconomicalCode ?? undefined,
              businessLicenseAssetId: lastExtension.businessLicenseAssetId ?? undefined,
            };
            await saveAddressExtension(cartExtensionInput);
            Logger.info("useCheckout", `[INIT] Successfully restored extension from localStorage to ${type} address`);
            continue;
          }

          Logger.info("useCheckout", `[INIT] No extension found in any source for ${type} address`);
          continue;
        }

        // Get extension from member address
        const memberExtension = await getAddressExtensionByAddressId(matchingMemberAddress.id, AddressSource.Member);

        if (!memberExtension?.personType) {
          Logger.info("useCheckout", `[INIT] Member address ${matchingMemberAddress.id} has no extension data to copy`);
          continue;
        }

        // Copy extension data from member to cart
        Logger.info(
          "useCheckout",
          `[INIT] Copying extension from member address ${matchingMemberAddress.id} to cart address ${cartAddress.id}`,
        );

        const cartExtensionInput: IAddressExtensionInput = {
          addressId: cartAddress.id,
          addressSource: AddressSource.Cart,
          guild: memberExtension.guild || undefined,
          storeName: memberExtension.storeName || undefined,
          latitude: memberExtension.latitude ?? undefined,
          longitude: memberExtension.longitude ?? undefined,
          personType: memberExtension.personType ?? undefined,
          naturalPersonName: memberExtension.naturalPersonName || undefined,
          naturalPersonFamily: memberExtension.naturalPersonFamily || undefined,
          naturalPersonNationalCode: memberExtension.naturalPersonNationalCode || undefined,
          nationalCardAssetId: memberExtension.nationalCardAssetId ?? undefined,
          legalPersonName: memberExtension.legalPersonName || undefined,
          legalPersonNationalCode: memberExtension.legalPersonNationalCode || undefined,
          legalPersonEconomicalCode: memberExtension.legalPersonEconomicalCode ?? undefined,
          businessLicenseAssetId: memberExtension.businessLicenseAssetId ?? undefined,
        };

        await saveAddressExtension(cartExtensionInput);
        Logger.info("useCheckout", `[INIT] Successfully copied extension to ${type} cart address`);
      } catch (err) {
        Logger.warn("useCheckout", `[INIT] Failed to ensure extension for ${type} address:`, err);
      }
    }
  }

  /**
   * Reconciles the cart's selected shipment against the currently available shipping methods.
   * If an admin changes a method's rate (or availability) while the customer is in checkout, the
   * cart holds a stale shipment snapshot and order placement fails with a generic cart validation
   * error — the customer is stuck until they manually re-select the delivery method.
   *
   * We detect the mismatch and (per product decision: auto-apply + notify):
   *  - re-apply the same method so VirtoCommerce re-rates the shipment to the current price, then
   *    notify the customer that the shipping cost changed; or
   *  - if the method is no longer offered, notify the customer to pick another one.
   *
   * Returns true when a change was detected (rate changed / method unavailable / lingering
   * validation error) — callers placing the order should abort so the customer can review the new
   * values first; false when the selected shipment already matches the current rate.
   */
  async function reconcileShipmentRate(): Promise<boolean> {
    const currentShipment = shipment.value;
    // Only relevant once a method is selected and available methods (address-dependent) are loaded.
    if (!currentShipment?.shipmentMethodCode || !availableShippingMethods.value.length) {
      return false;
    }

    const selectedId = `${currentShipment.shipmentMethodCode}_${currentShipment.shipmentMethodOption}`;
    const matching = availableShippingMethods.value.find((method) => method.id === selectedId);

    // Selected method no longer offered (deactivated, or not available for this address).
    if (!matching) {
      Logger.warn("useCheckout", `[SHIPMENT RECONCILE] Selected method ${selectedId} no longer available`);
      notifications.warning({
        text: t("shared.checkout.notifications.shipping_method_unavailable"),
        duration: 15000,
        single: true,
      });
      return true;
    }

    const oldAmount = currentShipment.price?.amount ?? 0;
    const newAmount = matching.price?.amount ?? 0;
    const priceChanged = oldAmount !== newAmount;
    // Also re-rate when a shipment-related validation error lingers even though the price matches.
    const hasShipmentValidationError = (cart.value?.validationErrors ?? []).some(
      (error) => /ship/i.test(error.errorCode ?? "") || /ship/i.test(error.objectType ?? ""),
    );

    if (!priceChanged && !hasShipmentValidationError) {
      return false;
    }

    Logger.info("useCheckout", "[SHIPMENT RECONCILE] Re-rating stale shipment", {
      selectedId,
      oldAmount,
      newAmount,
      priceChanged,
      hasShipmentValidationError,
    });

    let reRated = false;
    try {
      // Re-apply the method WITH the current available rate as the price. VirtoCommerce's
      // AddShipmentAsync validates the incoming shipment price against the available rates and
      // rejects (SHIPMENT_METHOD_PRICE_CHANGED) if it still carries the stale snapshot — so we
      // must send the new price explicitly for the cart to adopt it and clear the validation.
      // (The price is server-validated against available rates, so it can't be forged.)
      await updateShipment({
        id: currentShipment.id,
        shipmentMethodCode: currentShipment.shipmentMethodCode,
        shipmentMethodOption: currentShipment.shipmentMethodOption ?? undefined,
        price: matching.price?.amount,
      });
      reRated = true;
    } catch (err) {
      Logger.warn("useCheckout", "[SHIPMENT RECONCILE] Failed to re-rate shipment", err);
    }

    if (priceChanged) {
      notifications.warning({
        text: t("shared.checkout.notifications.shipping_price_changed", {
          old: currentShipment.price?.formattedAmount ?? String(oldAmount),
          new: matching.price?.formattedAmount ?? String(newAmount),
        }),
        duration: 15000,
        single: true,
      });
    } else if (!reRated) {
      // A shipment validation error lingered and we couldn't clear it automatically.
      notifications.warning({
        text: t("shared.checkout.notifications.shipping_method_unavailable"),
        duration: 15000,
        single: true,
      });
    }

    // A change was detected either way — signal callers to let the customer review before placing.
    return true;
  }

  /**
   * Verifies the cart's selected payment method is still offered. If an admin disabled it (or it
   * otherwise dropped out of the available methods) after the cart was loaded, notify the customer
   * to pick another one. Returns true when the selection is no longer valid.
   */
  async function reconcilePaymentMethod(): Promise<boolean> {
    const code = payment.value?.paymentGatewayCode;
    if (!code || !availablePaymentMethods.value.length) {
      return false;
    }

    const stillAvailable = availablePaymentMethods.value.some((method) => method.code === code);
    if (!stillAvailable) {
      Logger.warn("useCheckout", `[PAYMENT RECONCILE] Selected payment method ${code} no longer available`);
      notifications.warning({
        text: t("shared.checkout.notifications.payment_method_unavailable"),
        duration: 15000,
        single: true,
      });
      return true;
    }

    return false;
  }

  async function initialize(): Promise<void> {
    initialized.value = false;
    placedOrder.value = null;
    loading.value = true;

    await setCheckoutDefaults();

    // Ensure cart addresses have extension data (copy from member if needed)
    await ensureCartAddressExtensions();

    // Detect & auto-correct a stale shipping rate (e.g. admin changed the method price
    // mid-checkout) so the customer isn't blocked at order placement.
    await reconcileShipmentRate();

    analytics("beginCheckout", { ...cart.value!, items: selectedLineItems.value });

    loading.value = false;
    initialized.value = true;

    // Validate billing extension data after initialization
    await validateBillingExtension();
  }

  /**
   * Validates that the billing address has required extension data (personType).
   * This is called at checkout initialization and when billing address changes.
   */
  async function validateBillingExtension(): Promise<boolean> {
    billingExtensionValid.value = true;
    billingExtensionError.value = null;

    // Determine the effective billing address
    const effectiveBillingAddress = billingAddressEqualsShipping.value
      ? shipment.value?.deliveryAddress
      : payment.value?.billingAddress;

    if (!effectiveBillingAddress?.id) {
      // No address selected yet, skip validation
      return true;
    }

    try {
      // Determine the source based on whether it's from cart
      const addressSource = AddressSource.Cart;
      const extension = await getAddressExtensionByAddressId(effectiveBillingAddress.id, addressSource);

      // Debug logging for validation
      Logger.info("useCheckout", "[VALIDATE BILLING] Extension data:", {
        addressId: effectiveBillingAddress.id,
        personType: extension?.personType,
        legalPersonName: extension?.legalPersonName,
        legalPersonNationalCode: extension?.legalPersonNationalCode,
        legalPersonEconomicalCode: extension?.legalPersonEconomicalCode,
        naturalPersonName: extension?.naturalPersonName,
        naturalPersonFamily: extension?.naturalPersonFamily,
        naturalPersonNationalCode: extension?.naturalPersonNationalCode,
      });

      // Check if personType is set (required for billing)
      if (!extension?.personType) {
        billingExtensionValid.value = false;
        billingExtensionError.value = t("arad.address_extension.validation.billing_requires_person_type");
        return false;
      }

      // If personType is set, check related fields
      if (extension.personType === "natural") {
        if (!extension.naturalPersonName || !extension.naturalPersonFamily || !extension.naturalPersonNationalCode) {
          billingExtensionValid.value = false;
          billingExtensionError.value = t("arad.address_extension.validation.billing_missing_natural_fields");
          return false;
        }
      } else if (extension.personType === "legal") {
        // Only check legalPersonName and legalPersonNationalCode (economicalCode is hidden from users)
        if (!extension.legalPersonName || !extension.legalPersonNationalCode) {
          Logger.warn("useCheckout", "[VALIDATE BILLING] Legal fields missing:", {
            legalPersonName: extension.legalPersonName,
            legalPersonNationalCode: extension.legalPersonNationalCode,
          });
          billingExtensionValid.value = false;
          billingExtensionError.value = t("arad.address_extension.validation.billing_missing_legal_fields");
          return false;
        }
      }

      billingExtensionValid.value = true;
      return true;
    } catch (err) {
      Logger.warn("useCheckout", "Failed to validate billing extension:", err);
      // On error, allow checkout to proceed (extension validation is not critical)
      billingExtensionValid.value = true;
      return true;
    }
  }

  // Track previous address IDs to detect changes (e.g., from shipping method change)
  // Watch for shipping address ID changes and preserve extension data
  watch(
    () => shipment.value?.deliveryAddress?.id,
    async (newId, oldId) => {
      // If the shipping address ID has changed, copy extension from old ID to new ID
      if (oldId && newId && oldId !== newId) {
        // Skip validation on the next watcher trigger since we're changing address IDs
        skipValidationOnce.value = true;
        try {
          const oldExtension = await getAddressExtensionByAddressId(oldId, AddressSource.Cart);
          if (oldExtension && (oldExtension.guild || oldExtension.storeName || oldExtension.latitude)) {
            // Copy extension data from old address ID to new address ID
            const extensionInput: IAddressExtensionInput = {
              addressId: newId,
              addressSource: AddressSource.Cart,
              guild: oldExtension.guild || undefined,
              storeName: oldExtension.storeName || undefined,
              latitude: oldExtension.latitude ?? undefined,
              longitude: oldExtension.longitude ?? undefined,
              personType: oldExtension.personType ?? undefined,
              naturalPersonName: oldExtension.naturalPersonName || undefined,
              naturalPersonFamily: oldExtension.naturalPersonFamily || undefined,
              naturalPersonNationalCode: oldExtension.naturalPersonNationalCode || undefined,
              nationalCardAssetId: oldExtension.nationalCardAssetId ?? undefined,
              legalPersonName: oldExtension.legalPersonName || undefined,
              legalPersonNationalCode: oldExtension.legalPersonNationalCode || undefined,
              legalPersonEconomicalCode: oldExtension.legalPersonEconomicalCode || undefined,
              businessLicenseAssetId: oldExtension.businessLicenseAssetId ?? undefined,
            };
            await saveAddressExtension(extensionInput);
            // Re-validate billing extension after preservation if billing equals shipping
            if (billingAddressEqualsShipping.value && initialized.value) {
              await validateBillingExtension();
            }
          }
        } catch (err) {
          Logger.warn("useCheckout", "Failed to preserve shipping address extension:", err);
        } finally {
          // Allow validation on next trigger
          skipValidationOnce.value = false;
        }
      }
    },
  );

  // Watch for billing address ID changes and preserve extension data
  watch(
    () => payment.value?.billingAddress?.id,
    async (newId, oldId) => {
      // If the billing address ID has changed, copy extension from old ID to new ID
      if (oldId && newId && oldId !== newId) {
        // Skip validation on the next watcher trigger since we're changing address IDs
        skipValidationOnce.value = true;
        try {
          const oldExtension = await getAddressExtensionByAddressId(oldId, AddressSource.Cart);
          if (oldExtension && (oldExtension.guild || oldExtension.storeName || oldExtension.personType)) {
            // Copy extension data from old address ID to new address ID
            const extensionInput: IAddressExtensionInput = {
              addressId: newId,
              addressSource: AddressSource.Cart,
              guild: oldExtension.guild || undefined,
              storeName: oldExtension.storeName || undefined,
              latitude: oldExtension.latitude ?? undefined,
              longitude: oldExtension.longitude ?? undefined,
              personType: oldExtension.personType ?? undefined,
              naturalPersonName: oldExtension.naturalPersonName || undefined,
              naturalPersonFamily: oldExtension.naturalPersonFamily || undefined,
              naturalPersonNationalCode: oldExtension.naturalPersonNationalCode || undefined,
              nationalCardAssetId: oldExtension.nationalCardAssetId ?? undefined,
              legalPersonName: oldExtension.legalPersonName || undefined,
              legalPersonNationalCode: oldExtension.legalPersonNationalCode || undefined,
              legalPersonEconomicalCode: oldExtension.legalPersonEconomicalCode || undefined,
              businessLicenseAssetId: oldExtension.businessLicenseAssetId ?? undefined,
            };
            await saveAddressExtension(extensionInput);
            // Re-validate billing extension after preservation
            if (initialized.value) {
              await validateBillingExtension();
            }
          }
        } catch (err) {
          Logger.warn("useCheckout", "Failed to preserve billing address extension:", err);
        } finally {
          // Allow validation on next trigger
          skipValidationOnce.value = false;
        }
      }
    },
  );

  // Watch for address changes to re-validate billing extension
  // Need to watch deliveryAddress too since billing may equal shipping
  watch(
    [billingAddress, billingAddressEqualsShipping, deliveryAddress],
    async () => {
      // Skip validation on address ID changes - let the ID change watcher preserve extension first
      if (skipValidationOnce.value) {
        return;
      }
      if (initialized.value) {
        await validateBillingExtension();
      }
    },
    { deep: true },
  );

  /**
   * Explicit watcher for checkbox toggle.
   * When "same as shipping" is checked, ensure billing address reflects shipping address in payment object.
   * When unchecked, allow independent billing address selection.
   */
  watch(
    () => billingAddressEqualsShipping.value,
    async (newValue) => {
      if (newValue && shipment.value?.deliveryAddress?.id && initialized.value) {
        // Checkbox just checked - ensure payment object has billing address set to shipping address
        const hasBillingAddress = !!payment.value?.billingAddress?.id;
        const isSameAsShipping = payment.value?.billingAddress?.id === shipment.value.deliveryAddress.id;
        if (!hasBillingAddress || !isSameAsShipping) {
          await updatePayment({
            id: payment.value!.id,
            billingAddress: shipment.value.deliveryAddress,
          });
        }
      }
      // Always re-validate after toggle
      if (initialized.value) {
        await validateBillingExtension();
      }
    },
  );

  /**
   * Watcher for payment method changes.
   * Re-validates billing extension when payment method changes,
   * in case it affects billing address requirements.
   */
  watch(
    () => payment.value?.paymentGatewayCode,
    async () => {
      // Re-validate billing extension when payment method changes
      if (initialized.value) {
        await validateBillingExtension();
      }
    },
  );

  async function updateBillingOrDeliveryAddress(
    addressType: AddressType,
    inputAddress: InputAddressType,
  ): Promise<void> {
    if (
      addressType === AddressType.Billing &&
      (!payment.value?.billingAddress || !isEqualAddresses(payment.value?.billingAddress, inputAddress))
    ) {
      await updatePayment({
        id: payment.value?.id,
        billingAddress: inputAddress,
      });
    } else if (
      addressType === AddressType.Shipping &&
      (!shipment.value?.deliveryAddress || !isEqualAddresses(shipment.value?.deliveryAddress, inputAddress))
    ) {
      await updateShipment({
        id: shipment.value?.id,
        deliveryAddress: inputAddress,
      });
    }
  }

  function openAddOrUpdateAddressModal(
    addressType: AddressType,
    editableAddress?: MemberAddressType | CartAddressType,
  ): void {
    // Determine addressPurpose: if editing shipping and billingAddressEqualsShipping is true, use "both"
    let addressPurpose: "shipping" | "billing" | "both" = addressType === AddressType.Billing ? "billing" : "shipping";
    if (addressType === AddressType.Shipping && billingAddressEqualsShipping.value) {
      addressPurpose = "both";
    }

    openModal({
      component: ExtendedAddressModal,
      props: {
        address: editableAddress,
        addressPurpose,
        addressSource: AddressSource.Cart,

        async onResult(address: MemberAddressType, extensionState: IAddressExtensionFormState) {
          // Update the billing/delivery address in the cart (original flow - just copy data)
          const inputAddress: InputAddressType = {
            ...omit(address, ["id", "isDefault", "description", "isFavorite"]),
            addressType,
          };

          await updateBillingOrDeliveryAddress(addressType, inputAddress);

          // When "billing = shipping", also update the billing address with the same data
          // This ensures VirtoCommerce creates the billing address before we try to save extension
          if (addressPurpose === "both" && payment.value?.id) {
            await updatePayment({
              id: payment.value.id,
              billingAddress: inputAddress,
            });
            // Refetch cart to ensure we have the latest billing address ID
            await refetchCart();
          }

          // Get the actual address IDs from the cart after updates
          const shippingAddressId = shipment.value?.deliveryAddress?.id;
          const billingAddressId = payment.value?.billingAddress?.id;

          Logger.info(
            "useCheckout",
            `[SAVE EXTENSION] After updates - shipping: ${shippingAddressId}, billing: ${billingAddressId}`,
          );

          // Save extension data for Cart address using the actual cart address ID
          const actualAddressId = addressType === AddressType.Shipping ? shippingAddressId : billingAddressId;

          if (actualAddressId) {
            try {
              const extensionInput: IAddressExtensionInput = {
                addressId: actualAddressId,
                addressSource: AddressSource.Cart,
                guild: extensionState.guild || undefined,
                storeName: extensionState.storeName || undefined,
                latitude: extensionState.latitude ?? undefined,
                longitude: extensionState.longitude ?? undefined,
                personType: extensionState.personType ?? undefined,
                naturalPersonName: extensionState.naturalPersonName || undefined,
                naturalPersonFamily: extensionState.naturalPersonFamily || undefined,
                naturalPersonNationalCode: extensionState.naturalPersonNationalCode || undefined,
                nationalCardAssetId: extensionState.nationalCardAssetId ?? undefined,
                legalPersonName: extensionState.legalPersonName || undefined,
                legalPersonNationalCode: extensionState.legalPersonNationalCode || undefined,
                legalPersonEconomicalCode: extensionState.legalPersonEconomicalCode || undefined,
                businessLicenseAssetId: extensionState.businessLicenseAssetId ?? undefined,
              };
              await saveAddressExtension(extensionInput);

              // Also save to localStorage so it survives clearCart (only for logged-in users)
              if (isAuthenticated.value && user.value?.id) {
                if (addressType === AddressType.Shipping || addressPurpose === "both") {
                  lastShippingExtensionCache.value = createCachedExtension(extensionState);
                  Logger.info("useCheckout", "[SAVE EXTENSION] Saved shipping extension to localStorage cache");
                }
                if (addressType === AddressType.Billing || addressPurpose === "both") {
                  lastBillingExtensionCache.value = createCachedExtension(extensionState);
                  Logger.info("useCheckout", "[SAVE EXTENSION] Saved billing extension to localStorage cache");
                }
              }

              // When "billing = shipping", also save extension to the billing address
              // VirtoCommerce always creates separate addresses for shipping and billing,
              // so we need to save extension data to both addresses
              if (addressPurpose === "both" && billingAddressId && billingAddressId !== actualAddressId) {
                Logger.info(
                  "useCheckout",
                  `[SAVE EXTENSION] Saving extension to billing address ${billingAddressId} (same as shipping mode)`,
                );
                const billingExtensionInput: IAddressExtensionInput = {
                  ...extensionInput,
                  addressId: billingAddressId,
                };
                await saveAddressExtension(billingExtensionInput);
              }

              // Re-validate billing extension after saving
              await validateBillingExtension();

              // SAFE SYNC: If user explicitly selected this address from member addresses,
              // sync the extension back to the source member address.
              // This ensures billing info (personType, etc.) persists for future orders.
              // We ONLY do this when we have explicit ID tracking - never use content matching.
              const sourceMemberAddressId =
                addressType === AddressType.Shipping || addressPurpose === "both"
                  ? sourceMemberAddressIds.value.shipping
                  : sourceMemberAddressIds.value.billing;

              if (sourceMemberAddressId && isAuthenticated.value) {
                try {
                  Logger.info(
                    "useCheckout",
                    `[SYNC TO MEMBER] Syncing extension to source member address: ${sourceMemberAddressId}`,
                  );
                  const memberExtensionInput: IAddressExtensionInput = {
                    addressId: sourceMemberAddressId,
                    addressSource: AddressSource.Member,
                    guild: extensionState.guild || undefined,
                    storeName: extensionState.storeName || undefined,
                    latitude: extensionState.latitude ?? undefined,
                    longitude: extensionState.longitude ?? undefined,
                    personType: extensionState.personType ?? undefined,
                    naturalPersonName: extensionState.naturalPersonName || undefined,
                    naturalPersonFamily: extensionState.naturalPersonFamily || undefined,
                    naturalPersonNationalCode: extensionState.naturalPersonNationalCode || undefined,
                    nationalCardAssetId: extensionState.nationalCardAssetId ?? undefined,
                    legalPersonName: extensionState.legalPersonName || undefined,
                    legalPersonNationalCode: extensionState.legalPersonNationalCode || undefined,
                    legalPersonEconomicalCode: extensionState.legalPersonEconomicalCode ?? undefined,
                    businessLicenseAssetId: extensionState.businessLicenseAssetId ?? undefined,
                  };
                  await saveAddressExtension(memberExtensionInput);
                  Logger.info("useCheckout", "[SYNC TO MEMBER] Extension synced to member address successfully");
                } catch (err) {
                  // Non-critical: log warning but don't block checkout flow
                  Logger.warn("useCheckout", "Failed to sync extension to member address:", err);
                }
              } else if (isAuthenticated.value && !isExistAddress(address)) {
                // For NEW addresses (not selected from member), save to member profile
                // This ensures the address persists and can be reused in future orders
                try {
                  await saveAddressToMemberProfile(address, extensionState);
                } catch (err) {
                  // Non-critical: log warning but don't block checkout flow
                  Logger.warn("useCheckout", "Failed to save address to member profile:", err);
                }
              }
            } catch (err) {
              Logger.error("useCheckout", "Failed to save address extension:", err);
            }
          }

          closeModal();
        },
      },
    });
  }

  function openSelectAddressModal(addressType: AddressType): void {
    // Determine if we're in "billing = shipping" mode
    const isBillingEqualsShipping = addressType === AddressType.Shipping && billingAddressEqualsShipping.value;

    openModal({
      component: SelectAddressModal,

      props: {
        addresses: addresses.value,
        currentAddress:
          addressType === AddressType.Billing ? payment.value?.billingAddress : shipment.value?.deliveryAddress,
        isCorporateAddresses: isCorporateMember.value,

        async onResult(address?: MemberAddressType) {
          if (!address) {
            return;
          }

          // Store the source member address ID for later extension sync
          // This allows us to sync extension changes back to the correct member address
          if (address.id) {
            if (addressType === AddressType.Shipping) {
              sourceMemberAddressIds.value.shipping = address.id;
              Logger.info("useCheckout", `[SELECT ADDRESS] Tracked source member address for shipping: ${address.id}`);
            }
            if (addressType === AddressType.Billing || isBillingEqualsShipping) {
              sourceMemberAddressIds.value.billing = address.id;
              Logger.info("useCheckout", `[SELECT ADDRESS] Tracked source member address for billing: ${address.id}`);
            }
          }

          const inputAddress: InputAddressType = {
            ...omit(address, ["id", "isDefault", "description", "isFavorite"]),
            addressType,
          };

          await updateBillingOrDeliveryAddress(addressType, inputAddress);

          // When "billing = shipping", also update the billing address with the same data
          // This ensures VirtoCommerce creates the billing address before we try to save extension
          if (isBillingEqualsShipping && payment.value?.id) {
            await updatePayment({
              id: payment.value.id,
              billingAddress: inputAddress,
            });
            // Refetch cart to ensure we have the latest billing address ID
            await refetchCart();
          }

          // Get the actual address IDs from the cart after updates
          const shippingAddressId = shipment.value?.deliveryAddress?.id;
          const billingAddressId = payment.value?.billingAddress?.id;

          Logger.info(
            "useCheckout",
            `[SELECT ADDRESS] After updates - shipping: ${shippingAddressId}, billing: ${billingAddressId}`,
          );

          // Get the actual address ID for the primary address type
          const actualAddressId = addressType === AddressType.Shipping ? shippingAddressId : billingAddressId;

          // Copy extension data from Member address to Cart address
          if (address.id && actualAddressId) {
            try {
              const memberExtension = await getAddressExtensionByAddressId(address.id, AddressSource.Member);
              if (memberExtension) {
                const cartExtensionInput: IAddressExtensionInput = {
                  addressId: actualAddressId,
                  addressSource: AddressSource.Cart,
                  guild: memberExtension.guild || undefined,
                  storeName: memberExtension.storeName || undefined,
                  latitude: memberExtension.latitude ?? undefined,
                  longitude: memberExtension.longitude ?? undefined,
                  personType: memberExtension.personType ?? undefined,
                  naturalPersonName: memberExtension.naturalPersonName || undefined,
                  naturalPersonFamily: memberExtension.naturalPersonFamily || undefined,
                  naturalPersonNationalCode: memberExtension.naturalPersonNationalCode || undefined,
                  nationalCardAssetId: memberExtension.nationalCardAssetId ?? undefined,
                  legalPersonName: memberExtension.legalPersonName || undefined,
                  legalPersonNationalCode: memberExtension.legalPersonNationalCode || undefined,
                  legalPersonEconomicalCode: memberExtension.legalPersonEconomicalCode ?? undefined,
                  businessLicenseAssetId: memberExtension.businessLicenseAssetId ?? undefined,
                };
                await saveAddressExtension(cartExtensionInput);

                // When "billing = shipping", also save extension to the billing address
                // VirtoCommerce always creates separate addresses for shipping and billing,
                // so we need to save extension data to both addresses
                if (isBillingEqualsShipping && billingAddressId && billingAddressId !== actualAddressId) {
                  Logger.info(
                    "useCheckout",
                    `[SELECT ADDRESS] Saving extension to billing address ${billingAddressId} (same as shipping mode)`,
                  );
                  const billingExtensionInput: IAddressExtensionInput = {
                    ...cartExtensionInput,
                    addressId: billingAddressId,
                  };
                  await saveAddressExtension(billingExtensionInput);
                }

                // Re-validate billing extension after saving
                await validateBillingExtension();
              }
            } catch (err) {
              Logger.error("useCheckout", "Failed to copy address extension to cart:", err);
            }
          }
        },

        onAddNewAddress() {
          setTimeout(() => {
            openAddOrUpdateAddressModal(addressType);
          }, 500);
        },
      },
    });
  }

  async function fetchAddresses(): Promise<void> {
    if (!isAuthenticated.value) {
      return;
    }

    if (isCorporateMember.value) {
      await fetchOrganizationAddresses();
    } else {
      await fetchPersonalAddresses();
    }
  }

  function onDeliveryAddressChange(): void {
    addresses.value.length
      ? openSelectAddressModal(AddressType.Shipping)
      : openAddOrUpdateAddressModal(AddressType.Shipping, shipment.value?.deliveryAddress);
  }

  /**
   * Handles address selection for billing.
   * If "same as shipping" is checked, opens shipping address modal since billing uses shipping.
   * Otherwise opens billing address modal for independent selection.
   */
  function onBillingAddressChange(): void {
    if (billingAddressEqualsShipping.value && !allItemsAreDigital.value && !isShippingMethodBopis.value) {
      // When billing = shipping, open shipping address modal
      addresses.value.length
        ? openSelectAddressModal(AddressType.Shipping)
        : openAddOrUpdateAddressModal(AddressType.Shipping, shipment.value?.deliveryAddress);
    } else {
      // When billing is separate, open billing address modal
      addresses.value.length
        ? openSelectAddressModal(AddressType.Billing)
        : openAddOrUpdateAddressModal(AddressType.Billing, payment.value?.billingAddress);
    }
  }

  /**
   * Opens the edit modal for the effective billing address to fix billing extension errors.
   * If billingAddressEqualsShipping is true, edits the shipping address with "both" purpose.
   */
  function editBillingAddress(): void {
    if (billingAddressEqualsShipping.value) {
      // When billing = shipping, edit the shipping address with "both" purpose
      openAddOrUpdateAddressModal(AddressType.Shipping, shipment.value?.deliveryAddress);
    } else {
      // Edit the billing address directly
      openAddOrUpdateAddressModal(AddressType.Billing, payment.value?.billingAddress);
    }
  }

  /**
   * Saves a new address to the member profile immediately when created in cart.
   * Also saves the extension data to the member address.
   * This ensures addresses persist across cart clears and can be reused in future orders.
   */
  async function saveAddressToMemberProfile(
    address: MemberAddressType | CartAddressType,
    extensionState: IAddressExtensionFormState,
  ): Promise<void> {
    // Create member address from the cart address
    const memberAddress: MemberAddressType = {
      ...address,
      id: undefined, // Let the backend generate a new ID
      isDefault: false,
      isFavorite: false,
      addressType: AddressType.BillingAndShipping,
    };

    Logger.info("useCheckout", "[SAVE TO MEMBER] Saving address to member profile", {
      line1: memberAddress.line1,
      city: memberAddress.city,
    });

    // Capture existing IDs BEFORE save (for new address detection)
    const existingIds = new Set(addresses.value.map((a) => a.id));

    // Save address to member profile (these functions return void)
    if (isCorporateMember.value) {
      await addOrUpdateOrganizationAddresses([memberAddress]);
    } else {
      await addOrUpdatePersonalAddresses([memberAddress]);
    }

    // Refresh addresses list to get the saved address with its new ID
    await fetchAddresses();

    // Find the newly created address (ID that didn't exist before)
    const savedAddress = addresses.value.find((a) => a.id && !existingIds.has(a.id));

    if (savedAddress?.id) {
      Logger.info("useCheckout", "[SAVE TO MEMBER] Address saved with ID:", savedAddress.id);

      // Save extension data to the member address
      const memberExtensionInput: IAddressExtensionInput = {
        addressId: savedAddress.id,
        addressSource: AddressSource.Member,
        guild: extensionState.guild || undefined,
        storeName: extensionState.storeName || undefined,
        latitude: extensionState.latitude ?? undefined,
        longitude: extensionState.longitude ?? undefined,
        personType: extensionState.personType ?? undefined,
        naturalPersonName: extensionState.naturalPersonName || undefined,
        naturalPersonFamily: extensionState.naturalPersonFamily || undefined,
        naturalPersonNationalCode: extensionState.naturalPersonNationalCode || undefined,
        nationalCardAssetId: extensionState.nationalCardAssetId ?? undefined,
        legalPersonName: extensionState.legalPersonName || undefined,
        legalPersonNationalCode: extensionState.legalPersonNationalCode || undefined,
        legalPersonEconomicalCode: extensionState.legalPersonEconomicalCode ?? undefined,
        businessLicenseAssetId: extensionState.businessLicenseAssetId ?? undefined,
      };
      await saveAddressExtension(memberExtensionInput);
      Logger.info("useCheckout", "[SAVE TO MEMBER] Extension saved to member address");
    } else {
      Logger.warn("useCheckout", "[SAVE TO MEMBER] Could not find saved address after creation");
    }
  }

  function getNewAddresses(payload: {
    shippingAddress?: CartAddressType;
    billingAddress?: CartAddressType;
  }): MemberAddressType[] {
    const newAddresses: MemberAddressType[] = [];

    if (payload.shippingAddress && !isExistAddress(payload.shippingAddress)) {
      newAddresses.push({
        ...payload.shippingAddress,
        isDefault: false,
        isFavorite: false,
        addressType: AddressType.BillingAndShipping,
      });
    }
    if (
      payload.billingAddress &&
      !isExistAddress(payload.billingAddress) &&
      (!payload.shippingAddress || !isEqualAddresses(payload.shippingAddress, payload.billingAddress))
    ) {
      newAddresses.push({
        ...payload.billingAddress,
        isDefault: false,
        isFavorite: false,
        addressType: AddressType.BillingAndShipping,
      });
    }

    return newAddresses;
  }

  async function saveNewAddresses(payload: {
    shippingAddress?: CartAddressType;
    billingAddress?: CartAddressType;
  }): Promise<void> {
    // Store original Cart address IDs and their data BEFORE they get replaced by Member address IDs
    const cartAddressMap: { [key: string]: { id: string; address: CartAddressType } } = {};

    // Map cart addresses to their IDs for later extension copying
    if (payload.shippingAddress && !isExistAddress(payload.shippingAddress) && payload.shippingAddress.id) {
      cartAddressMap[`shipping`] = {
        id: payload.shippingAddress.id,
        address: payload.shippingAddress,
      };
    }
    if (
      payload.billingAddress &&
      !isExistAddress(payload.billingAddress) &&
      (!payload.shippingAddress || !isEqualAddresses(payload.shippingAddress, payload.billingAddress)) &&
      payload.billingAddress.id
    ) {
      cartAddressMap[`billing`] = {
        id: payload.billingAddress.id,
        address: payload.billingAddress,
      };
    }

    const newAddresses: MemberAddressType[] = getNewAddresses(payload);

    if (newAddresses.length === 0) {
      return;
    }

    // Capture existing IDs BEFORE save (for new address detection)
    const existingIds = new Set(addresses.value.map((a) => a.id));

    if (isCorporateMember.value) {
      await addOrUpdateOrganizationAddresses(newAddresses);
    } else {
      await addOrUpdatePersonalAddresses(newAddresses);
    }

    // Find the newly saved addresses (IDs that didn't exist before)
    const savedNewAddresses = addresses.value.filter((a) => a.id && !existingIds.has(a.id));

    // Copy extension data from Cart source to Member source for new addresses
    for (const savedAddress of savedNewAddresses) {
      if (!savedAddress.id) continue;

      try {
        // Find the matching cart address by comparing line1, city, postalCode
        let cartAddressId: string | undefined;

        for (const mapping of Object.values(cartAddressMap)) {
          const cartAddr = mapping.address;
          // Match by comparing address fields
          if (
            cartAddr.line1 === savedAddress.line1 &&
            cartAddr.city === savedAddress.city &&
            cartAddr.postalCode === savedAddress.postalCode
          ) {
            cartAddressId = mapping.id;
            break;
          }
        }

        if (!cartAddressId) continue;

        // Get extension data from cart using the original Cart address ID
        const cartExtension = await getAddressExtensionByAddressId(cartAddressId, AddressSource.Cart);
        if (cartExtension) {
          // Save to member source using the NEW Member address ID
          const memberExtensionInput: IAddressExtensionInput = {
            addressId: savedAddress.id,
            addressSource: AddressSource.Member,
            guild: cartExtension.guild || undefined,
            storeName: cartExtension.storeName || undefined,
            latitude: cartExtension.latitude ?? undefined,
            longitude: cartExtension.longitude ?? undefined,
            personType: cartExtension.personType ?? undefined,
            naturalPersonName: cartExtension.naturalPersonName || undefined,
            naturalPersonFamily: cartExtension.naturalPersonFamily || undefined,
            naturalPersonNationalCode: cartExtension.naturalPersonNationalCode || undefined,
            nationalCardAssetId: cartExtension.nationalCardAssetId ?? undefined,
            legalPersonName: cartExtension.legalPersonName || undefined,
            legalPersonNationalCode: cartExtension.legalPersonNationalCode || undefined,
            legalPersonEconomicalCode: cartExtension.legalPersonEconomicalCode ?? undefined,
            businessLicenseAssetId: cartExtension.businessLicenseAssetId ?? undefined,
          };
          await saveAddressExtension(memberExtensionInput);
        }
      } catch (err) {
        Logger.warn("useCheckout", "Failed to copy extension data to member address:", err);
      }
    }
  }

  async function prepareOrderData(): Promise<void> {
    Logger.info("useCheckout", "[PREPARE ORDER] Starting prepareOrderData");

    // ── Wallet split ──────────────────────────────────────────────────────────
    const walletState = useWalletPayment();
    const totalAmount = cart.value!.total.amount;
    const walletDeduction =
      walletState.useWallet.value && !walletState.isWalletOnly(totalAmount)
        ? walletState.walletDeductionAmount(totalAmount) // partial: wallet covers part
        : 0; // wallet-only or wallet off: no split needed here
    const mainPaymentAmount = walletDeduction > 0 ? totalAmount - walletDeduction : totalAmount;
    // ─────────────────────────────────────────────────────────────────────────

    // Update payment with required properties
    const filledPayment: InputPaymentType = {
      id: payment.value!.id,
      amount: mainPaymentAmount, // required
    };

    // Save shipping address as billing address
    if (!allItemsAreDigital.value && !isShippingMethodBopis.value && billingAddressEqualsShipping.value) {
      // Use the same address object (with ID) so extension data is preserved
      if (shipment.value?.deliveryAddress) {
        filledPayment.billingAddress = {
          ...shipment.value.deliveryAddress,
          addressType: AddressType.Billing,
        };
      }
    }

    await updatePayment(filledPayment);

    // After updatePayment, VirtoCommerce may have created a new billing address with a different ID
    // If billingEqualsShipping, we need to copy the shipping extension to the billing address
    if (!allItemsAreDigital.value && !isShippingMethodBopis.value && billingAddressEqualsShipping.value) {
      const shippingAddressId = shipment.value?.deliveryAddress?.id;
      const billingAddressId = payment.value?.billingAddress?.id;

      Logger.info("useCheckout", "[PREPARE ORDER] Checking billing extension copy", {
        shippingAddressId,
        billingAddressId,
        needsCopy: shippingAddressId && billingAddressId && shippingAddressId !== billingAddressId,
      });

      // If billing address has a different ID than shipping, copy the extension
      if (shippingAddressId && billingAddressId && shippingAddressId !== billingAddressId) {
        try {
          const shippingExtension = await getAddressExtensionByAddressId(shippingAddressId, AddressSource.Cart);
          if (shippingExtension) {
            Logger.info(
              "useCheckout",
              "[PREPARE ORDER] Copying shipping extension to billing address",
              billingAddressId,
            );
            const billingExtensionInput: IAddressExtensionInput = {
              addressId: billingAddressId,
              addressSource: AddressSource.Cart,
              guild: shippingExtension.guild || undefined,
              storeName: shippingExtension.storeName || undefined,
              latitude: shippingExtension.latitude ?? undefined,
              longitude: shippingExtension.longitude ?? undefined,
              personType: shippingExtension.personType ?? undefined,
              naturalPersonName: shippingExtension.naturalPersonName || undefined,
              naturalPersonFamily: shippingExtension.naturalPersonFamily || undefined,
              naturalPersonNationalCode: shippingExtension.naturalPersonNationalCode || undefined,
              nationalCardAssetId: shippingExtension.nationalCardAssetId ?? undefined,
              legalPersonName: shippingExtension.legalPersonName || undefined,
              legalPersonNationalCode: shippingExtension.legalPersonNationalCode || undefined,
              legalPersonEconomicalCode: shippingExtension.legalPersonEconomicalCode || undefined,
              businessLicenseAssetId: shippingExtension.businessLicenseAssetId ?? undefined,
            };
            await saveAddressExtension(billingExtensionInput);
            Logger.info("useCheckout", "[PREPARE ORDER] Successfully copied extension to billing address");
          }
        } catch (err) {
          Logger.warn("useCheckout", "[PREPARE ORDER] Failed to copy shipping extension to billing:", err);
        }
      }
    }

    // Reset purchase order number if it is unavailable
    if (!isPurchaseOrderNumberEnabled.value && purchaseOrderNumber.value) {
      await updatePurchaseOrderNumber("");
    }

    // If partial wallet: add a WalletBalance payment to the cart for the wallet portion.
    // The main payment (above) covers the remainder; wallet covers walletDeduction.
    if (walletDeduction > 0) {
      await updatePayment({
        paymentGatewayCode: "WalletBalance",
        amount: walletDeduction,
        billingAddress: filledPayment.billingAddress,
      });
      Logger.info("useCheckout", "[PREPARE ORDER] Added WalletBalance cart payment", { walletDeduction });
    }

    // Parallel saving of new addresses in account. Before cleaning shopping cart
    if (isAuthenticated.value) {
      void saveNewAddresses({
        shippingAddress:
          !allItemsAreDigital.value && !isShippingMethodBopis.value ? shipment.value!.deliveryAddress : undefined,
        billingAddress: payment.value!.billingAddress,
      });
    }
  }

  async function createOrderFromCart(): Promise<CustomerOrderType | null> {
    loading.value = true;
    Logger.info("useCheckout", "[CREATE ORDER] Starting createOrderFromCart");

    // Re-check the latest shipment & payment methods at submit time. An admin may have changed a
    // shipping rate or disabled a method after the cart was loaded; adopt the new values, notify
    // the customer, and abort this attempt so they can review and place the order with the new
    // values on the next click (rather than failing on a stale cart validation error).
    await refetchCart();
    const shipmentChanged = await reconcileShipmentRate();
    const paymentChanged = await reconcilePaymentMethod();
    if (shipmentChanged || paymentChanged) {
      Logger.info("useCheckout", "[CREATE ORDER] Aborted — methods changed, awaiting customer review", {
        shipmentChanged,
        paymentChanged,
      });
      loading.value = false;
      return null;
    }

    // Capture cart address IDs BEFORE order creation (they may change/be cleared after)
    const cartShippingAddressId = shipment.value?.deliveryAddress?.id;
    const cartBillingAddressId = payment.value?.billingAddress?.id;
    Logger.info("useCheckout", "[CREATE ORDER] Captured cart address IDs", {
      cartShippingAddressId,
      cartBillingAddressId,
    });

    await prepareOrderData();
    Logger.info("useCheckout", "[CREATE ORDER] prepareOrderData completed");

    try {
      // TODO remove as CustomerOrderType. Infer it from API
      placedOrder.value = (await _createOrderFromCart(cart.value!.id)) as CustomerOrderType;
    } catch (e) {
      Logger.error(`${useCheckout.name}.${createOrderFromCart.name}`, e);
    }

    let orderPayed = false;

    if (placedOrder.value) {
      // NOTE: Backend AddressExtensionOrderBuilder handles copying extensions from Cart to Order.
      // It extends CustomerOrderBuilder.PlaceCustomerOrderFromCartAsync to:
      // 1. Capture cart address IDs before VirtoCommerce converts cart to order
      // 2. Query order addresses by ShipmentId/PaymentInId (the addresses visible in admin)
      // 3. Copy extensions from Cart source to Order source
      // No frontend work needed here.

      // Wallet payment deduction is handled automatically on the backend via
      // AutoInitializeWalletPaymentHandler when the order is created.
      // No frontend initializePayment call needed for WalletBalance payments.

      // ── Admin impersonation branch ──────────────────────────────────────────
      // When an admin places the order on behalf of a customer the SMS notification
      // is sent entirely on the backend:
      //   OrderChangedEvent → SendSmsNotificationsOrderChangedEventHandler
      //                     → Hangfire: TrySendAdminPlacedOrderSmsNotificationAsync
      // No frontend involvement needed — no token juggling, no permission issues.
      const { operator } = useUser();
      if (operator.value && placedOrder.value) {
        const orderId = placedOrder.value.id;
        const orderNumber = placedOrder.value.number ?? orderId;

        try {
          await refetchCart();
        } catch {
          // non-fatal
        }
        clearState();
        clearExtensionCache();

        loading.value = false;

        try {
          await router.replace({
            name: "AdminOrderConfirmation",
            params: { orderId },
            state: { orderNumber },
          });
        } catch {
          // navigation failure is non-fatal — the order was placed successfully
        }

        return placedOrder.value;
      }
      // ── End admin impersonation branch ──────────────────────────────────────

      try {
        const result = await finalizePayment(placedOrder.value);
        orderPayed = result?.isSuccess ?? false;
      } catch (e) {
        Logger.error(`${useCheckout.name}.${createOrderFromCart.name}.paymentProcessor`, e);
        placedOrder.value = null;
        notifications.error({
          text: t("common.messages.payment_processing_error"),
          duration: 15000,
          single: true,
        });
        loading.value = false;
        return null;
      }

      await refetchCart();

      if (themeContext.value?.storeSettings?.defaultSelectedForCheckout && cart.value?.items.length) {
        selectCartItems(cart.value.items.map((item) => item.id));
      }

      clearState();

      // Clear extension cache after successful order - the extension data is now in the order
      clearExtensionCache();

      analytics("placeOrder", placedOrder.value);
      void pushHistoricalEvent({
        eventType: "placeOrder",
        sessionId: placedOrder.value.id,
        productIds: placedOrder.value.items?.map((item) => item.productId),
        storeId: globals.storeId,
      });

      await router.replace({ name: canPayNow.value && !orderPayed ? "CheckoutPayment" : "CheckoutCompleted" });
    } else {
      notifications.error({
        text: t("common.messages.creating_order_error"),
        duration: 15000,
        single: true,
      });
    }

    loading.value = false;

    return placedOrder.value;
  }

  function clearState() {
    clearGlobalCheckoutState();
    // Reset wallet toggle so it doesn't bleed into the next checkout session.
    useWalletPayment().resetWalletToggle();
  }

  watch(() => route.params.cartId, clearState);

  return {
    deliveryAddress,
    shipmentMethod,
    billingAddress,
    paymentMethod,
    comment,
    billingAddressEqualsShipping,
    purchaseOrderNumber,
    isValidDeliveryAddress,
    isValidBillingAddress,
    isValidShipmentMethod,
    isValidPaymentMethod,
    isValidShipment,
    isValidPayment,
    isValidCheckout,
    isPurchaseOrderNumberEnabled,
    selectedPaymentMethodGroupType,
    canPayNow,
    initialize,
    onDeliveryAddressChange,
    onBillingAddressChange,
    editBillingAddress,
    setPaymentMethod,
    createOrderFromCart,
    validateBillingExtension,
    billingExtensionError: readonly(billingExtensionError),
    billingExtensionValid: readonly(billingExtensionValid),
    loading: readonly(loading),
    changing: computed(() => commentChanging.value || purchaseOrderNumberChanging.value),
    placedOrder: computed(() => placedOrder.value),
    initialized: readonly(initialized),
    allOrderItemsAreDigital,
  };
}

const useCheckoutShared = createSharedComposableByArgs(_useCheckout, (args) => args?.[0] ?? "");

export function useCheckout() {
  const route = useRoute();
  const cartId = Array.isArray(route.params?.cartId) ? route.params?.cartId[0] : route.params?.cartId;

  return useCheckoutShared(cartId);
}
